import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Grid,
  Divider,
  Box,
  Button,
  CircularProgress,
  makeStyles
} from "@material-ui/core";
import axios from "axios";
import { appUrl } from "../../../../../../../utils/config";
import { useLocation } from "react-router-dom";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";

// Import all step components
import CMODetails from "./CMODetails";
import SiteDetails from "./SiteDetails";
import OptionsPermissions from "./OptionsPermissions";
import PurchasingDetails from "./PurchasingDetails";
import ShipmentConfigurations from "./Configurations";
import PlantDetails from "./PlantDetails";
import ErpToErpConfigurationSection from "../../shared/ErpToErpConfigurationSection";
import { DEFAULT_PRODUCT_TYPES } from "../../shared/erpToErpConfigData";
import useProductTypeNames from "../../shared/useProductTypeNames";
import CMOSuccessModal from '../CMOSuccessModal';

const useStyles = makeStyles((theme) => ({
  formField: {
    width: "100%",
    marginBottom: 0,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
    },
  },
  button: {
    minWidth: 180,
    // fontWeight: 600,
    // fontSize: 16,
    borderRadius: 30,
    // height: 44,
    textTransform: 'none',
  },
  backButton: {
    minWidth: 180,
    // fontWeight: 600,
    // fontSize: 16,
    borderRadius: 30,
    // height: 44,
    background: '#707070',
    color: '#fff',
    marginRight: theme.spacing(2),
  },
}));

const CMOReview = ({
  formData,
  // classes,
  handleNext,
  handleBack,
  handleSubmit,
  activeStep,
  stepsLength,
  setFormData,
  updateStepData,
  setActiveStep,
  markApiCallSuccessful
}) => {
  const classes = useStyles();
  const productTypeNamesById = useProductTypeNames();
  // Create read-only props for the step components
  const readOnlyProps = {
    classes,
    // Add a readOnly prop to disable all inputs
    readOnly: true,
  };

  // State to control the success popup
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successCMOData, setSuccessCMOData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isCMOUpdated, setIsCMOUpdated] = useState(false);
  const location = useLocation();

  const cmoDetailsResponse = JSON.parse(sessionStorage.getItem("cmoDetailsResponse"));
  // Handler for Save button
  const handleSaveClick = async () => {
    const payload = {
      organizationId: cmoDetailsResponse?.organizationId,
      organizationCode: cmoDetailsResponse?.organizationCode,
      email: cmoDetailsResponse?.email || formData?.cmoDetails?.adminDetails?.email,
      fullName: cmoDetailsResponse?.fullName || formData?.cmoDetails?.adminDetails?.fullName,
      phoneNumber: cmoDetailsResponse?.phoneNumber || formData?.cmoDetails?.adminDetails?.phone,
      collaboratorTypeId: 2,
      twoStepAuthentication: formData?.vendorPlantDetails?.twoStep,
      azureADSignIn: formData?.vendorPlantDetails?.azureAd,
      editPhoneNumber: formData?.vendorPlantDetails?.cannotEditPhone
    }


   
    try {
      setIsLoading(true);
      const response = await axios.post(`${appUrl}/user/sendCollaboratorInvitation`, payload);
      setSuccessCMOData({
        shortName: formData?.cmoDetails?.organizationDetails?.orgShortName,
        orgCode: cmoDetailsResponse?.organizationCode,
        email: cmoDetailsResponse?.email || formData?.cmoDetails?.adminDetails?.email,
        phone: cmoDetailsResponse?.phoneNumber || formData?.cmoDetails?.adminDetails?.phone,
      });
      setShowSuccessPopup(true);
      setIsCMOUpdated(true);
      sessionStorage.setItem("cmoUpdated", "true");
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for Ok button in popup
  const handlePopupOk = () => {
    setIsRedirecting(true);
    setShowSuccessPopup(false);

    // Only after the user has seen and acknowledged the success popup,
    // proceed with navigation by calling handleSubmit
    if (typeof handleSubmit === 'function') {
      handleSubmit();
    }
  };


  return (
    <div className={classes?.formContainer}>
      {/* Full Page Loader */}
      {(isLoading || isRedirecting) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <CircularProgress color="primary" thickness={7} />
        </div>
      )}
      <Paper elevation={0} className={`add-flow-view-cmo-wrapper ${classes?.reviewPaper}`}>
        <Grid container spacing={3}>
          {/* CMO Details Section */}
          <Grid item xs={12}>
            <Box mt={2} className={classes?.reviewSection}>
              <CMODetails formData={formData.cmoDetails} {...readOnlyProps} hideStepActions={true} />

            </Box>
          </Grid>


          {/* Site Details Section */}
          <Grid item xs={12}>
            <Box mt={2} className={classes?.reviewSection}>
              <SiteDetails formData={formData.vendorPlantDetails} {...readOnlyProps} hideStepActions={true} />
            </Box>
          </Grid>

          {/* Plant Details Section */}
          <Grid item xs={12}>
            <Box mt={2} className={classes?.reviewSection}>
              <PlantDetails formData={formData.vendorPlantDetails} {...readOnlyProps} hideStepActions={true} />
            </Box>
          </Grid>

          {/* Batch Configurations Section */}
          <Grid item xs={12}>
            <Box mt={2} className={classes?.reviewSection}>
              <ShipmentConfigurations formData={formData.vendorPlantDetails} {...readOnlyProps} hideStepActions={true} />
            </Box>
          </Grid>


          {/* Purchasing Details Section */}
          <Grid item xs={12}>
            <Box mt={2} className={classes?.reviewSection}>
              <PurchasingDetails formData={formData.vendorPlantDetails} {...readOnlyProps} hideStepActions={true} />
            </Box>
          </Grid>

          {/* ERP-to-ERP Configurations Section */}
          <Grid item xs={12}>
            <Box mt={2} className={classes?.reviewSection}>
              <ErpToErpConfigurationSection
                productTypes={(() => {
                  const vendorDetails =
                    formData?.vendorPlantDetails?.vendorPlantDetails || formData?.vendorPlantDetails || {};
                  const activatedTypes = (vendorDetails.vendorProductTypeConfigs || [])
                    .filter((pt) => pt.activated === true)
                    .map((pt) => ({
                      productTypeID: pt.productTypeID,
                      productTypeName: pt.productTypeName || productTypeNamesById[pt.productTypeID] || "",
                    }));
                  return activatedTypes.length > 0 ? activatedTypes : DEFAULT_PRODUCT_TYPES;
                })()}
                editMode={false}
                initialData={formData?.erpToErpConfig || {}}
                cmoName={formData?.cmoDetails?.cmoName}
                // The Add-flow's formData.vendorPlantDetails is a flat step-data shape
                // that never carries vendorMosymphonyID (unlike Update/View's nested
                // viewCMODetails-shaped formData) — without this fallback, the live
                // fetchAllVendorMessages call never fires here, so seedMessagesByModule
                // stays empty and the Build-Version dropdown has no matching option to
                // show even though the row's own buildVersion value is already saved.
                vendorMosymphonyID={
                  formData?.vendorPlantDetails?.vendorMosymphonyID || cmoDetailsResponse?.organizationId
                }
                brandOwnerMosymphonyID={formData?.vendorPlantDetails?.brandOwnerMosymphonyID}
              />
            </Box>
          </Grid>
          {/* <Divider /> */}

          {/* Options & Permissions Section */}
          {/* <Grid item xs={12}>
            <Box mt={2} className={classes?.reviewSection}>
              <OptionsPermissions formData={formData.vendorPlantDetails} {...readOnlyProps} hideStepActions={true} />
            </Box>
          </Grid>
          <Divider /> */}

        </Grid>
      </Paper>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: activeStep === 0 ? "center" : "space-between",
        }}
        className="stepper-navigation-btns"
        mt={4}
      >
        {activeStep !== 0 && (
          <>
            <Button
              variant="outlined"
              disabled={activeStep === 0}
              onClick={handleBack}
              className={classes.button}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
          </>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveClick}
          className={classes.button}
          disabled={isCMOUpdated}
          endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIosIcon />}
        >
          Send Invitation
        </Button>
      </Box>

      {/* Success Popup Modal */}
      <CMOSuccessModal
        open={showSuccessPopup}
        onClose={handlePopupOk}
        cmoData={successCMOData}
      />
    </div>
  );
};

export default CMOReview;